import React, { useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    fetchBookingSummaryRatePerResource,
    ServiceCenterBookingSummaryReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { BookingSummaryEntry } from "../../statisticsTypes"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useExportBookingsDuration } from "../../hooks/useExportBookingsDuration"
import { ResourceTypeFilter } from "../common/ResourceTypeFilter"

const selectBookingSummaries = (data: ServiceCenterBookingSummaryReportResponse) => data?.bookingSummaries || []

export function BookingDuration() {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const [selectedResource, setSelectedResource] = useState<ResourceType>(ResourceType.ROOM)
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const tableInstanceRef = useRef<MRT_TableInstance<BookingSummaryEntry>>(null)

    const bookingSummariesResult = useApiQuery(fetchBookingSummaryRatePerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY_PER_RESOURCE,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            resourceType: selectedResource,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectBookingSummaries,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(bookingSummariesResult)

    const columns = useMemo<MRT_ColumnDef<BookingSummaryEntry>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-resource-title"),
                accessorKey: "resource.displayName",
            }),

            columnFactory.nameColumn({
                header: l10n.getString(
                    "service-center-reports-statistic-booking-duration-table-number-of-bookings-title",
                ),
                accessorKey: "numberOfBookings",
            }),
            columnFactory.nameColumn({
                header: l10n.getString(
                    "service-center-reports-statistic-booking-duration-table-average-duration-title",
                ),
                accessorKey: "averageDuration",
                Cell: ({ row }) => {
                    return dateTimeFactory.formatDuration(
                        dateTimeFactory.fromDurationString(row.original.averageDuration),
                    )
                },
            }),
        ],
        [l10n, columnFactory],
    )

    const exportBookingDurations = useExportBookingsDuration({
        data: bookingSummariesResult.data,
        startDate: dateState.startDate || today,
        endDate: dateState.endDate || today,
    })

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                initialState={{ density: "spacious" }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton
                            onClick={() => {
                                exportBookingDurations()
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <LocalizedStrict
                            id="service-center-reports-statistic-occupancy-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </LocalizedStrict>
                        <ResourceTypeFilter value={selectedResource} onChange={setSelectedResource} />
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
